/* eslint-disable complexity */
import * as THREE from 'three';
import { Vector3 } from 'three';
import { IBuildingProviderContext, ITexture } from '../../@types/ContextProviderRelated';
import { EElementType, EOpeningDirection, EOrientation } from '../../@types/Enums';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { IWall } from '../../models/abstract/Wall';
import CompassModel from '../../models/standard/CompassModel';
import { IDoor } from '../../models/standard/DoorModel';
import ElementModel from '../../models/standard/ElementModel';
import FloorModel from '../../models/standard/FloorModel';
import Model2DModel from '../../models/standard/Model2DModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';
import RoomModel from '../../models/standard/RoomModel';
import SpaceModel from '../../models/standard/SpaceModel';
import StandardWallModel, { IStandardWall } from '../../models/standard/StandardWallModel';
import TilesModel from '../../models/standard/TileModel';
import { IWindow } from '../../models/standard/WindowModel';
import { OLD_WALL_THICKNESS } from '../../utils/constants';
import filesApiService from '../../utils/filesApiService';
import { parseFilesApiModelRequest } from '../../utils/jsonParser';
import { IGltfExtendedBuilding, IGltfFloorplan } from '../@types/Gltf';
import { calculateObjectPositionOnWall } from '../utils/calculateObjectPositionOnWall';
import { fetchLegacyTextureFromApi } from '../utils/dataHandling/fetchTextureFromApi';
import { specialModelSrcSet } from './gltfToStructure';

// --- GEOMETRY HELPERS FOR MIGRATION ---
const EPS = 1e-4;
const samePt = (a: Vector3, b: Vector3) => Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;

function signedArea2D(points: Vector3[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y - points[j].x * points[i].y;
  }
  return area / 2;
}

function intersectLines2D(p1: Vector3, dir1: Vector3, p2: Vector3, dir2: Vector3): Vector3 | null {
  const cross = dir1.x * dir2.y - dir1.y * dir2.x;
  if (Math.abs(cross) < 1e-6) return null;
  const t = ((p2.x - p1.x) * dir2.y - (p2.y - p1.y) * dir2.x) / cross;
  return new Vector3(p1.x + t * dir1.x, p1.y + t * dir1.y, 0);
}

// --- EXISTING PARSERS ---

const DEFAULT_FLOOR_OPTIONS = {
  tileLength: 20,
  tileWidth: 20,
  tileColor: '#ffffff',
  floorRotation: 0,
  tileGap: 0.5,
  gapColor: '#555555',
};

const _areFloorOptionsDiffersFromDefault = (gltfFloorplan: IGltfFloorplan): boolean => {
  const { floor } = gltfFloorplan;
  return Object.entries(DEFAULT_FLOOR_OPTIONS).some(([key, value]) => floor[key as keyof typeof floor] !== value);
};

const _parseTiles = (gltfFloorplan: IGltfFloorplan): TilesModel | undefined => {
  const { tileLength, tileWidth, floorRotation, tileGap, gapColor, floorTextureType } = gltfFloorplan.floor;
  const areOptionsDiffer = _areFloorOptionsDiffersFromDefault(gltfFloorplan);
  return floorTextureType !== undefined || areOptionsDiffer ? new TilesModel(tileLength, tileWidth, floorRotation, tileGap, gapColor) : undefined;
};

const _parseTexture = (gltfFloorplan: IGltfFloorplan): ITexture | undefined => {
  const { tileColor, floorTextureType } = gltfFloorplan.floor;
  const areOptionsDiffer = _areFloorOptionsDiffersFromDefault(gltfFloorplan);
  if (floorTextureType === undefined && !areOptionsDiffer) return;
  return { id: -1, name: '', material: null, color: tileColor, rotation: 0, offset: 0 } as ITexture;
};

/**
 * UPDATED: Parses walls and applies the Inner-to-Center Line migration.
 */
const _parseWallsWithRelatedObjects = (gltfFloorplan: IGltfFloorplan) => {
  const wallsStand: Array<IWall> = [];
  const parsedStandardWalls: Array<StandardWallModel> = [];

  const rawWalls = gltfFloorplan.walls;
  if (rawWalls.length < 3) return { wallsStand, parsedStandardWalls };

  // 1. Vertices extrahieren (Annahme: GLTF Walls sind ein geschlossener Loop)
  const vertices = rawWalls.map((w) => new THREE.Vector3(w.x, w.y, 0));

  // 2. Winding bestimmen (CW oder CCW)
  const area = signedArea2D(vertices);
  const isCW = area <= 0;

  // 3. Offset-Linien berechnen (Inflation nach außen um halbe Dicke)
  const offsetLines = vertices.map((p, i) => {
    const nextP = vertices[(i + 1) % vertices.length];
    const dir = new Vector3().subVectors(nextP, p).normalize();
    const outwardNormal = isCW ? new Vector3(-dir.y, dir.x, 0) : new Vector3(dir.y, -dir.x, 0);

    // Wir nutzen OLD_WALL_THICKNESS (meist 0.15) für den Shift
    const shiftVec = outwardNormal.clone().multiplyScalar(OLD_WALL_THICKNESS / 2);
    return { p: p.clone().add(shiftVec), dir, shiftVec };
  });

  // 4. Neue Eckpunkte durch Schnittpunkte berechnen
  const newVertices: Vector3[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const prev = offsetLines[(i - 1 + vertices.length) % vertices.length];
    const curr = offsetLines[i];
    const hit = intersectLines2D(prev.p, prev.dir, curr.p, curr.dir);
    newVertices.push(hit || curr.p.clone());
  }

  // 5. Modelle instanziieren mit den neuen Center-Line Koordinaten
  for (let i = 0; i < rawWalls.length; i++) {
    const tmpWall = rawWalls[i];
    const shiftedStart = newVertices[i];
    const shiftedEnd = newVertices[(i + 1) % newVertices.length];
    const currentShiftVec = offsetLines[i].shiftVec;

    // Windows
    const tmpWindows: IWindow[] = tmpWall.windows.map((w) => {
      const pos = calculateObjectPositionOnWall(shiftedStart, shiftedEnd, w);
      // Wir müssen den Shift-Vektor addieren, damit das Fenster auf der verschobenen Wand bleibt
      pos.add(currentShiftVec);
      return { position: pos, length: w.width, depth: OLD_WALL_THICKNESS, angle: 0 };
    });

    // Doors
    const tmpDoors: IDoor[] = tmpWall.doors.map((d) => {
      const pos = calculateObjectPositionOnWall(shiftedStart, shiftedEnd, d);
      pos.add(currentShiftVec);
      return {
        position: pos,
        depth: OLD_WALL_THICKNESS,
        angle: 0,
        orientation: d.orientation === 'Inside' ? EOrientation.INWARD : EOrientation.OUTWARD,
        openingDirection: d.openingDirection === 'LeftToRight' ? EOpeningDirection.RIGHT : EOpeningDirection.LEFT,
        length: d.width,
      };
    });

    const tmpStandardWall = new StandardWallModel(shiftedStart, shiftedEnd, OLD_WALL_THICKNESS, tmpWindows as any, tmpDoors as any);

    parsedStandardWalls.push(tmpStandardWall);
    wallsStand.push(tmpStandardWall);
  }

  return { wallsStand, parsedStandardWalls };
};

const _parsePartitionalWalls = (gltfFloorplan: IGltfFloorplan) => {
  const wallsPart: Array<IWall> = [];
  const parsedPartionalWalls: PartitionWallModel[] = [];

  gltfFloorplan.partionalWalls.forEach((wallEntry) => {
    const startPoint = new THREE.Vector3(wallEntry.startX, wallEntry.startY, 0);
    const endPoint = new THREE.Vector3(wallEntry.endX, wallEntry.endY, 0);

    // Auch für Trennwände: Verschiebung um halbe Tiefe nach "Links" (Standard-Normalen-Richtung)
    const dir = new Vector3().subVectors(endPoint, startPoint).normalize();
    const normal = new Vector3(-dir.y, dir.x, 0);
    const shift = normal.multiplyScalar(wallEntry.depth / 2);

    const tmpWall = new PartitionWallModel(startPoint.clone().add(shift), endPoint.clone().add(shift), wallEntry.depth);
    parsedPartionalWalls.push(tmpWall);
    wallsPart.push(tmpWall);
  });

  return { wallsPart, parsedPartionalWalls };
};

// ... Rest der Hilfsfunktionen (_parseElements, _parseTextFields, _parse2DModels) bleiben gleich ...
// [Beibehalten wie im Original-Code]

const _parseElements = (gltfFloorplan: IGltfFloorplan): Array<ElementModel> => {
  const parsedElements: Array<ElementModel> = [];
  gltfFloorplan.elements.forEach((element) => {
    const position = new THREE.Vector3(element.xPos, element.yPos);
    let texture = undefined;
    if (element.color) {
      texture = { id: -1, name: '', material: null, color: element.color, rotation: 0, offset: 0 } as ITexture;
    }
    const rotation = (-element.rotation + 2 * Math.PI) % (2 * Math.PI);
    if (element.src === '/img/sampleProducts/heizung2.png' || specialModelSrcSet.has(element.src)) {
      parsedElements.push(new ElementModel(EElementType.RECTANGLE, element.length, element.depth, position, rotation, texture, element.description, undefined, undefined));
    } else if (element.src === '/img/sampleProducts/heizung3.png') {
      parsedElements.push(new ElementModel(EElementType.CIRCLE, element.length, element.depth, position, rotation, texture, element.description, undefined, undefined));
    }
  });
  return parsedElements;
};

const _parseTextFields = (gltfFloorplan: IGltfFloorplan): Array<ElementModel> => {
  const parsedTextFields: Array<ElementModel> = [];
  const estimatedFontAdjustement = 0.03;
  gltfFloorplan.textFields.forEach((textField) => {
    const estimatedXOffset = textField.fontSize - 0.25;
    textField.fontSize -= estimatedFontAdjustement;
    const position = new THREE.Vector3(textField.xPos - estimatedXOffset, textField.yPos);
    const estimatedWidth = (textField.text?.length ?? 0) * (textField.fontSize ?? 0.5) * 0.2;
    let texture = undefined;
    if (textField.color) {
      texture = { id: -1, name: '', material: null, color: textField.color, rotation: 0, offset: 0 } as ITexture;
    }
    parsedTextFields.push(
      new ElementModel(EElementType.RECTANGLE, estimatedWidth * 6.4, textField.fontSize * 2, position, textField.rotation, texture, textField.text, true, textField.fontSize),
    );
  });
  return parsedTextFields;
};

const _parse2DModels = async (gltfFloorplan: IGltfFloorplan): Promise<Array<Model2DModel>> => {
  const fetchedModels = await Promise.all(
    gltfFloorplan.models2D.map(async (model2D) => {
      const position = new THREE.Vector3(model2D.xPos, model2D.yPos);
      const response = await filesApiService.dataRequest(`model/${model2D.toFileId}`);
      const modelData = parseFilesApiModelRequest(response);
      const svgPath = await filesApiService.getSVGFileFromModel(modelData.id);
      let texture = await fetchLegacyTextureFromApi(modelData.texture);
      if (texture === null && model2D.color) {
        texture = { id: -1, name: '', material: null, color: model2D.color, rotation: 0, offset: 0 } as ITexture;
      }
      const rotation = (-model2D.rotation + ((Number(response.rotation2D) || 0) * Math.PI) / 180 + 2 * Math.PI) % (2 * Math.PI);
      return { modelData, svgPath, position, rotation, texture };
    }),
  );
  return fetchedModels.map(
    (m) =>
      new Model2DModel(
        m.modelData.id,
        m.modelData.name,
        m.svgPath,
        m.modelData.fileFormat,
        m.modelData.fileSize,
        m.modelData.length,
        m.modelData.depth,
        m.modelData.object,
        m.position,
        m.rotation,
        m.texture,
      ),
  );
};

function orderWallsIntoLoop(walls: IStandardWall[]): IStandardWall[] {
  if (walls.length < 3) return walls;
  const unused = new Set(walls.map((w) => w.uuid));
  const byUuid = new Map(walls.map((w) => [w.uuid, w]));
  const first = walls[0];
  const ordered: IStandardWall[] = [first];
  unused.delete(first.uuid);
  const start = first.startPoint.clone();
  let cursor = first.endPoint.clone();
  while (unused.size) {
    let next: IStandardWall | undefined;
    for (const uuid of unused) {
      const w = byUuid.get(uuid)!;
      if (samePt(w.startPoint, cursor) || samePt(w.endPoint, cursor)) {
        next = w;
        break;
      }
    }
    if (!next) break;
    ordered.push(next);
    unused.delete(next.uuid);
    cursor = samePt(next.startPoint, cursor) ? next.endPoint.clone() : next.startPoint.clone();
  }
  return ordered;
}

export const gltfStructureToModels = async (gltfFloorplan: IGltfFloorplan): Promise<IGltfExtendedBuilding> => {
  const parsedTiles = _parseTiles(gltfFloorplan);
  const parsedTexture = _parseTexture(gltfFloorplan);
  const parsedFloor = new FloorModel(parsedTiles, parsedTexture);

  const { wallsStand, parsedStandardWalls } = _parseWallsWithRelatedObjects(gltfFloorplan);
  const { wallsPart, parsedPartionalWalls } = _parsePartitionalWalls(gltfFloorplan);
  const parsedWalls: Array<IWall> = wallsStand.concat(wallsPart);

  let parsedElements = _parseElements(gltfFloorplan);
  const parsedTextFields = _parseTextFields(gltfFloorplan);
  parsedElements = parsedElements.concat(parsedTextFields);

  const parsed2DModels = await _parse2DModels(gltfFloorplan);

  const boundaries: PhysicalBoundaryModel[] = [];
  if (parsedStandardWalls.length >= 3) {
    const orderedWalls = orderWallsIntoLoop(parsedStandardWalls);
    const boundary = new PhysicalBoundaryModel(orderedWalls as any);
    const edges: BoundaryEdge[] = [];
    let cursor = orderedWalls[0].startPoint.clone();
    for (const w of orderedWalls) {
      const reversed = !samePt(w.startPoint, cursor);
      cursor = reversed ? w.startPoint.clone() : w.endPoint.clone();
      edges.push(new BoundaryEdge(w, 0, 1, 'A', reversed));
    }
    boundary.setEdges(edges);
    boundaries.push(boundary);
  }

  const space = new SpaceModel(parsedFloor, [], parsedElements, parsed2DModels, boundaries, parsedWalls);
  const parsedRoom = new RoomModel('', [space]);

  const parsedBuilding: IBuildingProviderContext = {
    rooms: [parsedRoom],
    walls: parsedWalls,
    products: [],
    elements: [],
    models2D: [],
    compass: new CompassModel(),
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.UNKNOWN,
    nonFetchableEntities: [],
    relations: [],
  };

  return { building: parsedBuilding, standardWalls: parsedStandardWalls, partitionWalls: parsedPartionalWalls };
};
